import { useState, useMemo } from "react";
import {
    Button,
    Card,
    CardBody,
    Chip,
    Input,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    CheckboxGroup,
    Checkbox,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Spinner,
} from "@heroui/react";
import {
    Search,
    Shield,
    Plus,
    Edit,
    Trash2,
    MoreVertical,
    Lock,
    CheckCircle,
    AlertCircle,
    RefreshCw,
} from "lucide-react";
import { useRoles, usePermissions, useCreateRole, useUpdateRole, useDeleteRole, useAssignPermissionsToRole, useRemovePermissionsFromRole } from "@src/presentation/hooks/rbac";
import { ToastService } from "@src/shared/services/toast.service";
import type { Role } from "@src/domain/rbac/entities/role.entity";
import type { Permission } from "@src/domain/rbac/entities/permission.entity";

export default function RolesPage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        isActive: true,
    });
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onOpenChange: onDeleteOpenChange, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isPermissionsOpen, onOpen: onPermissionsOpen, onOpenChange: onPermissionsOpenChange, onClose: onPermissionsClose } = useDisclosure();

    // Fetch data
    const { data: roles = [], isLoading: rolesLoading, refetch } = useRoles();
    const { data: permissions = [], isLoading: permissionsLoading } = usePermissions();

    // Mutations
    const createRoleMutation = useCreateRole();
    const updateRoleMutation = useUpdateRole();
    const deleteRoleMutation = useDeleteRole();
    const assignPermissionsMutation = useAssignPermissionsToRole();
    const removePermissionsMutation = useRemovePermissionsFromRole();

    // Filter roles based on search
    const filteredRoles = useMemo(() => {
        return roles.filter((role) =>
            role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            role.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [roles, searchQuery]);

    // Stats
    const stats = useMemo(() => ({
        total: roles.length,
        active: roles.filter((r) => r.isActive).length,
        inactive: roles.filter((r) => !r.isActive).length,
        totalPermissions: permissions.length,
    }), [roles, permissions]);

    // Open modal for creating new role
    const handleCreate = () => {
        setSelectedRole(null);
        setFormData({ name: "", description: "", isActive: true });
        setErrors({});
        onOpen();
    };

    // Open modal for editing role
    const handleEdit = (role: Role) => {
        setSelectedRole(role);
        setFormData({
            name: role.name,
            description: role.description,
            isActive: role.isActive,
        });
        setErrors({});
        onOpen();
    };

    // Open delete confirmation modal
    const handleDeleteClick = (role: Role) => {
        setSelectedRole(role);
        onDeleteOpen();
    };

    // Open permissions modal
    const handleManagePermissions = (role: Role) => {
        setSelectedRole(role);
        setSelectedPermissions(role.permissions);
        onPermissionsOpen();
    };

    // Save role (create or update)
    const handleSave = async () => {
        // Validation
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) newErrors.name = "Role name is required";
        if (!formData.description.trim()) newErrors.description = "Description is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            if (selectedRole) {
                // Update existing role
                await updateRoleMutation.mutateAsync({
                    roleId: selectedRole.roleId,
                    data: formData,
                });
                ToastService.success("Role updated successfully");
            } else {
                // Create new role
                await createRoleMutation.mutateAsync({
                    name: formData.name,
                    description: formData.description,
                });
                ToastService.success("Role created successfully");
            }
            onClose();
        } catch (error: any) {
            ToastService.error(error.message || "Failed to save role");
        }
    };

    // Delete role
    const handleDelete = async () => {
        if (!selectedRole) return;

        try {
            await deleteRoleMutation.mutateAsync(selectedRole.roleId);
            ToastService.success("Role deleted successfully");
            onDeleteClose();
        } catch (error: any) {
            ToastService.error(error.message || "Failed to delete role");
        }
    };

    // Save permissions
    const handleSavePermissions = async () => {
        if (!selectedRole) return;

        try {
            // Calculate permissions to add and remove
            const currentPermissions = selectedRole.permissions;
            const permissionsToAdd = selectedPermissions.filter(
                (p) => !currentPermissions.includes(p)
            );
            const permissionsToRemove = currentPermissions.filter(
                (p) => !selectedPermissions.includes(p)
            );

            // Execute both operations if needed
            const promises = [];

            if (permissionsToAdd.length > 0) {
                promises.push(
                    assignPermissionsMutation.mutateAsync({
                        roleId: selectedRole.roleId,
                        permissionNames: permissionsToAdd,
                    })
                );
            }

            if (permissionsToRemove.length > 0) {
                promises.push(
                    removePermissionsMutation.mutateAsync({
                        roleId: selectedRole.roleId,
                        permissionNames: permissionsToRemove,
                    })
                );
            }

            if (promises.length > 0) {
                await Promise.all(promises);
                ToastService.success("Permissions updated successfully");
            } else {
                ToastService.info("No changes to save");
            }

            onPermissionsClose();
        } catch (error: any) {
            ToastService.error(error.message || "Failed to update permissions");
        }
    };

    // Group permissions by resource
    const groupedPermissions = useMemo(() => {
        return permissions.reduce((acc, permission) => {
            const resource = permission.resource;
            if (!acc[resource]) {
                acc[resource] = [];
            }
            acc[resource].push(permission);
            return acc;
        }, {} as Record<string, Permission[]>);
    }, [permissions]);

    if (rolesLoading || permissionsLoading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-24">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded bg-primary">
                                <Shield className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Roles Management</h1>
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                                {filteredRoles.length} {filteredRoles.length === 1 ? "role" : "roles"}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={rolesLoading}
                                startContent={<RefreshCw className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleCreate}
                                startContent={<Plus className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Create Role</span>
                                <span className="sm:hidden">Create Role</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Stats Overview */}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Card className="border border-border rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10">
                                        <Shield className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-default-500">Total Roles</p>
                                        <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border border-success/20 bg-success/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-success/10">
                                        <CheckCircle className="h-5 w-5 text-success" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-success">Active Roles</p>
                                        <p className="text-2xl font-bold text-success">{stats.active}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border border-default/20 bg-default/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-default/10">
                                        <AlertCircle className="h-5 w-5 text-default-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-default-500">Inactive Roles</p>
                                        <p className="text-2xl font-bold text-foreground">{stats.inactive}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border border-warning/20 bg-warning/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded bg-warning/10">
                                        <Lock className="h-5 w-5 text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-warning">Total Permissions</p>
                                        <p className="text-2xl font-bold text-warning">{stats.totalPermissions}</p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Filters and Table */}
                    <Card className="border border-border rounded shadow-none">
                        <CardBody className="p-0">
                            {/* Search Bar */}
                            <div className="border-b border-border p-4">
                                <Input
                                    placeholder="Search roles by name or description..."
                                    value={searchQuery}
                                    onValueChange={setSearchQuery}
                                    startContent={<Search className="h-4 w-4 text-default-400" />}
                                    className="max-w-md"
                                    classNames={{
                                        inputWrapper: "rounded border border-border",
                                    }}
                                />
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto">
                                <Table
                                    aria-label="Roles table"
                                    removeWrapper
                                    classNames={{
                                        th: "bg-default-50 text-default-600 font-semibold",
                                        td: "py-4",
                                    }}
                                >
                                    <TableHeader>
                                        <TableColumn>ROLE</TableColumn>
                                        <TableColumn className="hidden md:table-cell">DESCRIPTION</TableColumn>
                                        <TableColumn className="hidden md:table-cell">PERMISSIONS</TableColumn>
                                        <TableColumn className="hidden md:table-cell">CREATED</TableColumn>
                                        <TableColumn>ACTIONS</TableColumn>
                                    </TableHeader>
                                    <TableBody emptyContent="No roles found">
                                        {filteredRoles.map((role) => (
                                            <TableRow key={role.roleId} className="hover:bg-default-50 min-h-[56px]">
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                                                            <Shield className="h-4 w-4 text-primary" />
                                                        </div>
                                                        <span className="font-medium text-foreground">{role.displayName}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <span className="text-sm text-default-500">{role.description}</span>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color="primary"
                                                        className="rounded"
                                                    >
                                                        {role.permissions.length} permissions
                                                    </Chip>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <span className="text-sm text-default-500">
                                                        {new Date(role.createdAt).toLocaleDateString()}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() => handleManagePermissions(role)}
                                                            className="rounded"
                                                        >
                                                            <Lock className="h-4 w-4" />
                                                        </Button>
                                                        <Dropdown classNames={{
                                                            content: 'rounded'

                                                        }}>
                                                            <DropdownTrigger>
                                                                <Button
                                                                    isIconOnly
                                                                    size="sm"
                                                                    variant="light"
                                                                    className="rounded"
                                                                >
                                                                    <MoreVertical className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownTrigger>
                                                            <DropdownMenu aria-label="Role actions">
                                                                <DropdownItem
                                                                    key="edit"
                                                                    startContent={<Edit className="h-4 w-4" />}
                                                                    onPress={() => handleEdit(role)}
                                                                    className="rounded"
                                                                >
                                                                    Edit
                                                                </DropdownItem>
                                                                <DropdownItem
                                                                    key="delete"
                                                                    className="text-danger rounded"
                                                                    color="danger"
                                                                    startContent={<Trash2 className="h-4 w-4" />}
                                                                    onPress={() => handleDeleteClick(role)}
                                                                >
                                                                    Delete
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </Dropdown>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </main>

            {/* Create/Edit Modal */}
            <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" classNames={{ base: "rounded" }}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="border-b border-border">
                                {selectedRole ? "Edit Role" : "Create New Role"}
                            </ModalHeader>
                            <ModalBody className="py-6">
                                <div className="space-y-4">
                                    <Input
                                        label="Role Name"
                                        placeholder="Enter role name"
                                        value={formData.name}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, name: value })
                                        }
                                        isInvalid={!!errors.name}
                                        errorMessage={errors.name}
                                        isDisabled={!!selectedRole}
                                        classNames={{
                                            inputWrapper: "rounded border border-border",
                                        }}
                                    />
                                    <Input
                                        label="Description"
                                        placeholder="Enter role description"
                                        value={formData.description}
                                        onValueChange={(value) =>
                                            setFormData({ ...formData, description: value })
                                        }
                                        isInvalid={!!errors.description}
                                        errorMessage={errors.description}
                                        classNames={{
                                            inputWrapper: "rounded border border-border",
                                        }}
                                    />
                                    {selectedRole && (
                                        <Checkbox
                                            isSelected={formData.isActive}
                                            onValueChange={(value) =>
                                                setFormData({ ...formData, isActive: value })
                                            }
                                        >
                                            Active
                                        </Checkbox>
                                    )}
                                </div>
                            </ModalBody>
                            <ModalFooter className="border-t border-border">
                                <Button variant="flat" onPress={onClose} className="rounded">
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={handleSave}
                                    isLoading={createRoleMutation.isPending || updateRoleMutation.isPending}
                                    className="rounded"
                                >
                                    {selectedRole ? "Update" : "Create"}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Manage Permissions Modal */}
            <Modal isOpen={isPermissionsOpen} onOpenChange={onPermissionsOpenChange} size="2xl" scrollBehavior="inside" classNames={{ base: "rounded" }}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="border-b border-border">
                                <div>
                                    <h3 className="text-lg font-semibold">Manage Permissions</h3>
                                    <p className="text-sm font-normal text-default-500">{selectedRole?.name}</p>
                                </div>
                            </ModalHeader>
                            <ModalBody className="py-6">
                                <CheckboxGroup
                                    value={selectedPermissions}
                                    onValueChange={setSelectedPermissions}
                                    className="space-y-4"
                                >
                                    {Object.entries(groupedPermissions).map(([resource, perms]) => (
                                        <div key={resource} className="space-y-3">
                                            <div className="flex items-center gap-2 pb-2 border-b border-border">
                                                <Shield className="h-4 w-4 text-primary" />
                                                <h3 className="text-sm font-semibold uppercase text-primary">
                                                    {resource}
                                                </h3>
                                                <Chip size="sm" variant="flat" color="primary" className="rounded">
                                                    {perms.length}
                                                </Chip>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3 pl-6">
                                                {perms.map((permission) => (
                                                    <Checkbox key={permission.name} value={permission.name}>
                                                        <div>
                                                            <p className="text-sm font-medium">{permission.name}</p>
                                                            <p className="text-xs text-default-500">
                                                                {permission.description}
                                                            </p>
                                                        </div>
                                                    </Checkbox>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </CheckboxGroup>
                            </ModalBody>
                            <ModalFooter className="border-t border-border">
                                <Button variant="flat" onPress={onClose} className="rounded">
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={handleSavePermissions}
                                    isLoading={assignPermissionsMutation.isPending || removePermissionsMutation.isPending}
                                    className="rounded"
                                >
                                    Save Permissions
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteOpen} onOpenChange={onDeleteOpenChange} classNames={{ base: "rounded" }}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="border-b border-border">Confirm Delete</ModalHeader>
                            <ModalBody className="py-6">
                                <p className="text-foreground">
                                    Are you sure you want to delete the role{" "}
                                    <strong className="font-semibold">{selectedRole?.name}</strong>? This action cannot be
                                    undone.
                                </p>
                            </ModalBody>
                            <ModalFooter className="border-t border-border">
                                <Button variant="flat" onPress={onClose} className="rounded">
                                    Cancel
                                </Button>
                                <Button
                                    color="danger"
                                    onPress={handleDelete}
                                    isLoading={deleteRoleMutation.isPending}
                                    className="rounded"
                                >
                                    Delete
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
}
